import { PropsWithChildren, ReactNode } from 'react';
import { Radio } from '../../../components/radio/radio';
import { Field, FieldProps } from '../../../components/field/field';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { Typography } from '../../../components/typography/typography';
import parse from 'html-react-parser';
import classNames from 'classnames';

type ConfigPropertyProps<T = unknown> = T &
  PropsWithChildren<{ name?: string; property: string; description?: string; helper?: ReactNode; dataCy?: string }>;

export const ConfigSection = ({
  children,
  title,
  className,
  dataCy,
}: {
  children?: ReactNode;
  title?: string;
  className?: string;
  dataCy?: string;
}) => {
  return (
    <div className={classNames('flex flex-col gap-4 flex-nowrap', className)} data-cy={dataCy}>
      {title && (
        <h5 data-cy="config-section-title">
          <strong>{title}</strong>
        </h5>
      )}
      {children}
    </div>
  );
};

export const ConfigProperty = ({ property, helper, description, children, dataCy }: ConfigPropertyProps) => {
  return (
    <div className="flex gap-2 items-start shrink-0" data-cy={dataCy}>
      <div className="basis-1/4 flex gap-1 items-center">
        <div data-cy="config-property-name">{property}</div>

        {helper && <div data-cy="config-property-helper">{helper}</div>}
      </div>

      <div className="basis-3/4">
        <div className="flex flex-col gap-1">
          <div>{children}</div>

          {description && (
            <Typography type="text-xs" className="!text-grey-700 !italic" dataCy="config-property-description">
              {parse(description)}
            </Typography>
          )}
        </div>
      </div>
    </div>
  );
};

export const ConfigBinaryProperty = ({
  name,
  value,
  onChange,
  ...props
}: ConfigPropertyProps<{ value: boolean; onChange?: (v: boolean) => void }>) => {
  return (
    <ConfigProperty {...props}>
      <div className="flex gap-2">
        <Radio
          name={name}
          label="No"
          isChecked={!value}
          value="0"
          onChange={(checked) => checked && onChange?.(false)}
          dataCy="config-property-binary-no"
        />
        <Radio
          name={name}
          label="Yes"
          isChecked={value}
          value="1"
          onChange={(checked) => checked && onChange?.(true)}
          dataCy="config-property-binary-yes"
        />
      </div>
    </ConfigProperty>
  );
};

export const ConfigCheckboxProperty = ({
  name,
  description,
  value,
  onChange,
  ...props
}: ConfigPropertyProps<{ value: boolean; onChange?: (v: boolean) => void }>) => {
  return (
    <ConfigProperty {...props}>
      <Checkbox
        name={name}
        isChecked={value}
        onChange={(v) => onChange?.(v)}
        label={
          <Typography type="text-xs" className="!text-grey-700 !italic">
            {description}
          </Typography>
        }
        dataCy="config-property-checkbox"
      />
    </ConfigProperty>
  );
};

/* v8 ignore start */
/**
 * @deprecated Use ConfigProperty with Field component directly instead.
 * @description This will be removed in a refactor of Settings page. This is to avoid the wraper and being able to use all Field props without adding boilerplate.
 * @example
 * ```tsx
 * <ConfigProperty property="Name" description="Name of the property">
 *   <Field name="name" type="text" value={value} onChange={(v) => onChange?.(v)} disabled={disabled} />
 * </ConfigProperty>
 * ```
 */
// TODO: remove this component in a refactor of Settings page.
export const ConfigInputProperty = ({
  type,
  name,
  value,
  onChange,
  disabled = false,
  ...props
}: ConfigPropertyProps<{ type: FieldProps['type']; value: any; onChange?: (v: any) => void; disabled?: boolean }>) => {
  return (
    <ConfigProperty {...props}>
      <Field name={name} type={type} value={value} onChange={(v) => onChange?.(v)} disabled={disabled} />
    </ConfigProperty>
  );
};
/* v8 ignore stop */
